import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Save, Timer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import apontamentoService from '@/services/apontamento';
import ordemProducaoService, { type OrdemProducao } from '@/services/ordemProducao';
import produtoService, { type Produto } from '@/services/produto';
import loteService, { type Lote } from '@/services/lote';
import { getUuidCandidate, isUuid } from '@/utils/uuid';

const TIPOS_PRODUTO_ACABADO = ['PA', '04'];
type ApiValidationResponse = {
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
};

type ApontamentoFormState = {
  op: string;
  tipo: 'producao' | 'consumo';
  produto: string;
  lote: string;
  quantidade: number;
  quantidade_refugo: number;
  funcionario: string;
  responsavel: string;
  tempo_real_minutos: number | string;
  observacoes: string;
};

const FORM_VAZIO: ApontamentoFormState = { op: '', tipo: 'producao', produto: '', lote: '', quantidade: 0, quantidade_refugo: 0, funcionario: '', responsavel: '', tempo_real_minutos: '', observacoes: '' };

const fieldLabels: Record<string, string> = {
  op: 'Ordem de produção',
  produto: 'Material consumido',
  lote: 'Lote',
  quantidade: 'Quantidade',
  quantidade_produzida: 'Quantidade produzida',
  quantidade_refugo: 'Quantidade refugo',
  funcionario: 'Funcionário',
  responsavel: 'Responsável',
  tempo_real_minutos: 'Tempo real',
  observacoes: 'Observações',
};

function extractErrorMessages(error: unknown): string[] {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiValidationResponse | Record<string, unknown> | undefined;
    if (data && 'errors' in data && data.errors && typeof data.errors === 'object') {
      return Object.entries(data.errors as Record<string, string[]>).flatMap(([field, messages]) =>
        messages.map((message) => `${fieldLabels[field] || field}: ${message}`),
      );
    }
    if (data && 'error' in data && typeof data.error === 'string') return [data.error];
    if (data && 'message' in data && typeof data.message === 'string') return [data.message];
  }

  return ['Não foi possível registrar o apontamento. Verifique os dados e tente novamente.'];
}

function opProdutoLabel(op?: OrdemProducao) {
  if (!op) return '';
  const codigo = op.produto_codigo || op.produto?.codigo || '-';
  const descricao = op.produto_nome || op.produto_descricao || op.produto?.descricao || '';
  return `${codigo} - ${descricao}`.trim();
}

export default function ApontamentoForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(FORM_VAZIO);
  const [ordens, setOrdens] = useState<OrdemProducao[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  const ordensValidas = useMemo(() => ordens.filter((op) => getUuidCandidate(op)), [ordens]);
  const produtosMP = useMemo(() => produtos.filter((p) => getUuidCandidate(p) && !TIPOS_PRODUTO_ACABADO.includes(p.tipo_produto_codigo || '')), [produtos]);
  const lotesValidos = useMemo(() => lotes.filter((lote) => getUuidCandidate(lote)), [lotes]);
  const ordemSelecionada = useMemo(
    () => ordensValidas.find((op) => getUuidCandidate(op) === form.op),
    [form.op, ordensValidas],
  );

  useEffect(() => {
    ordemProducaoService.listar().then(setOrdens).catch(console.error);
    produtoService.listar().then(setProdutos).catch(console.error);
  }, []);

  const onProdutoChange = async (produtoId: string) => {
    setForm((current) => ({ ...current, produto: produtoId, lote: '' }));
    if (!produtoId) return setLotes([]);
    loteService.listar({ produto: produtoId, status: 'LIVRE' }).then(setLotes).catch(console.error);
  };

  const onOrdemChange = (opId: string) => {
    setForm((current) => ({ ...current, op: opId, produto: '', lote: '' }));
    setLotes([]);
  };

  const salvar = async () => {
    const errosFormulario: string[] = [];
    if (!isUuid(form.op)) errosFormulario.push('Ordem de produção: selecione uma OP válida.');
    if (!form.quantidade || Number(form.quantidade) <= 0) errosFormulario.push('Quantidade: informe um valor maior que zero.');
    if (form.tipo === 'producao' && !form.funcionario.trim()) errosFormulario.push('Funcionário: informe o responsável pelo apontamento de produção.');
    if (form.tipo === 'consumo' && !isUuid(form.produto)) errosFormulario.push('Material consumido: selecione o produto/material consumido.');
    if (form.tipo === 'consumo' && !form.responsavel.trim()) errosFormulario.push('Responsável: informe o responsável pelo consumo.');

    if (errosFormulario.length > 0) {
      setErros(errosFormulario);
      return;
    }

    setErros([]);
    setSalvando(true);
    try {
      if (form.tipo === 'producao') {
        await apontamentoService.producao.registrar({
          op: form.op,
          quantidade_produzida: Number(form.quantidade),
          quantidade_refugo: Number(form.quantidade_refugo) || 0,
          funcionario: form.funcionario,
          tempo_real_minutos: form.tempo_real_minutos ? Number(form.tempo_real_minutos) : null,
          observacoes: form.observacoes,
        });
      } else {
        await apontamentoService.consumo.registrar({
          op: form.op,
          produto: form.produto,
          lote: form.lote || null,
          quantidade: Number(form.quantidade),
          responsavel: form.responsavel,
        });
      }
      navigate('/producao/apontamentos');
    } catch (error) {
      console.error('Erro ao registrar apontamento', error);
      setErros(extractErrorMessages(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader icon={<Timer size={21} />} title="Novo Apontamento" description="Registre apontamento de produção ou consumo de materiais." actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/apontamentos')}><ArrowLeft size={16} /> Voltar</Button>} />
      <Card className="nx-page-card nx-sngpc-form-card">
        {erros.length > 0 ? (
          <div className="nx-form-alert nx-form-alert--error" role="alert">
            <strong>Não foi possível registrar o apontamento.</strong>
            <ul>
              {erros.map((erro, index) => (
                <li key={`${erro}-${index}`}>{erro}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Dados do apontamento</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Tipo" htmlFor="apontamento-tipo"><Select id="apontamento-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as 'producao' | 'consumo' })}><option value="producao">Produção</option><option value="consumo">Consumo</option></Select></Field>
            <Field label="Ordem de produção *" htmlFor="apontamento-op" className="nx-sngpc-field--full"><Select id="apontamento-op" value={form.op} onChange={(e) => onOrdemChange(e.target.value)} required><option value="">Selecione</option>{ordensValidas.map((op) => <option key={op.id} value={getUuidCandidate(op) || ''}>{op.numero} - {op.produto_nome || op.produto_codigo || ''}</option>)}</Select></Field>
            <Field label="Produto da OP" htmlFor="apontamento-produto-op" className="nx-sngpc-field--full"><Input id="apontamento-produto-op" value={opProdutoLabel(ordemSelecionada)} placeholder="Selecione a ordem de produção" readOnly disabled /></Field>
            {form.tipo === 'consumo' ? (
              <>
                <Field label="Material consumido *" htmlFor="apontamento-produto"><Select id="apontamento-produto" value={form.produto} onChange={(e) => onProdutoChange(e.target.value)} required><option value="">Selecione</option>{produtosMP.map((p) => <option key={p.id} value={getUuidCandidate(p) || ''}>{p.codigo} - {p.descricao}</option>)}</Select></Field>
                <Field label="Lote" htmlFor="apontamento-lote"><Select id="apontamento-lote" value={form.lote} onChange={(e) => setForm({ ...form, lote: e.target.value })}><option value="">Selecione</option>{lotesValidos.map((lote) => <option key={lote.id} value={getUuidCandidate(lote) || ''}>{lote.numero_lote}</option>)}</Select></Field>
                <Field label="Responsável *" htmlFor="apontamento-responsavel"><Input id="apontamento-responsavel" value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })} required /></Field>
              </>
            ) : (
              <>
                <Field label="Funcionário *" htmlFor="apontamento-funcionario"><Input id="apontamento-funcionario" value={form.funcionario} onChange={(e) => setForm({ ...form, funcionario: e.target.value })} required /></Field>
                <Field label="Qtd. refugo" htmlFor="apontamento-refugo"><DecimalInput id="apontamento-refugo" value={form.quantidade_refugo} onValueChange={(value) => setForm({ ...form, quantidade_refugo: value ?? 0 })} /></Field>
                <Field label="Tempo real (min)" htmlFor="apontamento-tempo"><DecimalInput id="apontamento-tempo" value={form.tempo_real_minutos} onValueChange={(value) => setForm({ ...form, tempo_real_minutos: value ?? '' })} /></Field>
              </>
            )}
            <Field label="Quantidade *" htmlFor="apontamento-quantidade"><DecimalInput id="apontamento-quantidade" value={form.quantidade} onValueChange={(value) => setForm({ ...form, quantidade: value ?? 0 })} required /></Field>
            <Field label="Observações" htmlFor="apontamento-observacoes" className="nx-sngpc-field--full"><Textarea id="apontamento-observacoes" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={3} /></Field>
          </div>
        </section>
      </Card>
      <FormStickyFooter cancelLabel="Cancelar" onCancel={() => navigate('/producao/apontamentos')} saveLabel="Salvar" loading={salvando} saveIcon={<Save size={16} />} />
    </form>
  );
}
